import React, { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import { toast } from "sonner";
import {
  MousePointer2, Pen, Highlighter, ArrowUpRight, Square, Minus, Type,
  Undo2, Trash2, Loader2, Check, X,
} from "lucide-react";

const COLORS = ["#e11d48", "#2563eb", "#16a34a", "#f59e0b", "#000000"];
const TOOLS = [
  { id: "select", label: "Select", Icon: MousePointer2 },
  { id: "pen", label: "Pen", Icon: Pen },
  { id: "highlight", label: "Highlighter", Icon: Highlighter },
  { id: "arrow", label: "Arrow", Icon: ArrowUpRight },
  { id: "rect", label: "Rectangle", Icon: Square },
  { id: "line", label: "Line", Icon: Minus },
  { id: "text", label: "Text", Icon: Type },
];

const hexToRgba = (hex, a) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

function makeArrow(x1, y1, x2, y2, color, width) {
  const line = new fabric.Line([x1, y1, x2, y2], {
    stroke: color, strokeWidth: width, selectable: false, evented: false,
  });
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = Math.max(10, width * 3.5);
  const tri = new fabric.Triangle({
    left: x2, top: y2, originX: "center", originY: "center",
    width: head, height: head, fill: color,
    angle: (angle * 180) / Math.PI + 90, selectable: false, evented: false,
  });
  const g = new fabric.Group([line, tri], { selectable: true });
  g._markup = true;
  return g;
}

export default function MarkupEditor({ pages, isPdf, filename, onCancel, onSave }) {
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const wrapRef = useRef(null);
  const canvasEls = useRef([]);
  const canvases = useRef([]);
  const history = useRef([]); // ordered {canvas, object} for undo
  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const widthRef = useRef(strokeWidth);

  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { widthRef.current = strokeWidth; }, [strokeWidth]);

  // Build one fabric canvas per page.
  useEffect(() => {
    const maxW = Math.min((wrapRef.current?.clientWidth || 760) - 8, 900);
    let disposed = false;
    const made = [];

    pages.forEach((pg, i) => {
      const el = canvasEls.current[i];
      if (!el) return;
      const displayW = Math.min(pg.width, maxW);
      const scale = displayW / pg.width;
      const c = new fabric.Canvas(el, {
        width: displayW,
        height: Math.round(pg.height * scale),
        selection: false,
        preserveObjectStacking: true,
      });
      c._pageScale = scale;
      fabric.Image.fromURL(pg.url, (img) => {
        if (disposed) return;
        img.scaleToWidth(displayW);
        c.setBackgroundImage(img, c.renderAll.bind(c));
      });
      wireCanvas(c);
      made.push(c);
    });
    canvases.current = made;
    setReady(true);

    return () => {
      disposed = true;
      made.forEach((c) => { try { c.dispose(); } catch (e) { /* noop */ } });
      canvases.current = [];
      history.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  // Re-apply drawing mode / brush whenever tool/color/width change.
  useEffect(() => {
    canvases.current.forEach((c) => applyTool(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, color, strokeWidth, ready]);

  const applyTool = (c) => {
    const t = toolRef.current;
    const drawing = t === "pen" || t === "highlight";
    c.isDrawingMode = drawing;
    c.selection = t === "select";
    if (drawing) {
      const brush = new fabric.PencilBrush(c);
      brush.color = t === "highlight" ? hexToRgba(colorRef.current, 0.4) : colorRef.current;
      brush.width = t === "highlight" ? widthRef.current * 6 : widthRef.current;
      c.freeDrawingBrush = brush;
    }
    c.forEachObject((o) => { o.selectable = t === "select"; o.evented = t === "select"; });
    c.requestRenderAll();
  };

  const pushHistory = (c, obj) => { obj._markup = true; history.current.push({ canvas: c, object: obj }); };

  const wireCanvas = (c) => {
    let draft = null;
    let start = null;

    c.on("path:created", (e) => { pushHistory(c, e.path); });

    c.on("mouse:down", (opt) => {
      const t = toolRef.current;
      if (t === "select" || t === "pen" || t === "highlight") return;
      const p = c.getPointer(opt.e);
      start = { x: p.x, y: p.y };
      const col = colorRef.current;
      const w = widthRef.current;
      if (t === "text") {
        const it = new fabric.IText("Text", {
          left: p.x, top: p.y, fill: col, fontSize: Math.max(16, w * 6), fontFamily: "sans-serif",
        });
        it._markup = true;
        c.add(it);
        pushHistory(c, it);
        c.setActiveObject(it);
        it.enterEditing();
        it.selectAll();
        setTool("select");
        return;
      }
      if (t === "rect") {
        draft = new fabric.Rect({ left: p.x, top: p.y, width: 1, height: 1, fill: "transparent", stroke: col, strokeWidth: w, selectable: false, evented: false });
      } else if (t === "line") {
        draft = new fabric.Line([p.x, p.y, p.x, p.y], { stroke: col, strokeWidth: w, selectable: false, evented: false });
      } else if (t === "arrow") {
        draft = new fabric.Line([p.x, p.y, p.x, p.y], { stroke: col, strokeWidth: w, selectable: false, evented: false });
      }
      if (draft) c.add(draft);
    });

    c.on("mouse:move", (opt) => {
      if (!draft || !start) return;
      const p = c.getPointer(opt.e);
      const t = toolRef.current;
      if (t === "rect") {
        draft.set({ width: Math.abs(p.x - start.x), height: Math.abs(p.y - start.y), left: Math.min(p.x, start.x), top: Math.min(p.y, start.y) });
      } else {
        draft.set({ x2: p.x, y2: p.y });
      }
      c.requestRenderAll();
    });

    c.on("mouse:up", (opt) => {
      if (!draft || !start) { draft = null; start = null; return; }
      const t = toolRef.current;
      const p = c.getPointer(opt.e);
      if (t === "arrow") {
        c.remove(draft);
        if (Math.hypot(p.x - start.x, p.y - start.y) > 4) {
          const arr = makeArrow(start.x, start.y, p.x, p.y, colorRef.current, widthRef.current);
          c.add(arr);
          pushHistory(c, arr);
        }
      } else {
        draft.set({ selectable: false, evented: false });
        pushHistory(c, draft);
      }
      draft = null;
      start = null;
      c.requestRenderAll();
    });
  };

  const undo = () => {
    const last = history.current.pop();
    if (!last) return;
    last.canvas.remove(last.object);
    last.canvas.requestRenderAll();
  };

  const clearAll = () => {
    canvases.current.forEach((c) => {
      c.getObjects().slice().forEach((o) => c.remove(o));
      c.requestRenderAll();
    });
    history.current = [];
  };

  const handleSave = async () => {
    if (!canvases.current.length) return;
    if (!history.current.length) { toast.error("Add at least one markup before saving"); return; }
    setSaving(true);
    try {
      canvases.current.forEach((c) => { c.discardActiveObject(); c.requestRenderAll(); });
      const dataUrls = canvases.current.map((c, i) => c.toDataURL({ format: "png", multiplier: pages[i].width / c.getWidth() }));
      const baseName = filename.replace(/\.[^.]+$/, "");
      let file;
      if (isPdf) {
        const { jsPDF } = await import("jspdf");
        let doc;
        dataUrls.forEach((durl, i) => {
          const w = pages[i].width;
          const h = pages[i].height;
          const orient = w >= h ? "landscape" : "portrait";
          if (i === 0) doc = new jsPDF({ orientation: orient, unit: "px", format: [w, h], compress: true });
          else doc.addPage([w, h], orient);
          doc.addImage(durl, "PNG", 0, 0, w, h);
        });
        const blob = doc.output("blob");
        file = new File([blob], `${baseName} (markup).pdf`, { type: "application/pdf" });
      } else {
        const blob = await (await fetch(dataUrls[0])).blob();
        file = new File([blob], `${baseName} (markup).png`, { type: "image/png" });
      }
      await onSave(file);
    } catch (e) {
      toast.error("Could not save markup");
      if (process.env.NODE_ENV === "development") console.warn("markup save failed", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="markup-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap py-2 border-b border-zinc-200">
        {TOOLS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTool(id)} title={label} data-testid={`markup-tool-${id}`}
            className={`h-8 w-8 flex items-center justify-center rounded-sm border transition-colors ${
              tool === id ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 hover:bg-zinc-100"}`}>
            <Icon className="h-4 w-4" />
          </button>
        ))}
        <span className="w-px h-6 bg-zinc-200 mx-1" />
        {COLORS.map((cHex) => (
          <button key={cHex} onClick={() => setColor(cHex)} title={cHex} data-testid={`markup-color-${cHex.replace("#", "")}`}
            className={`h-6 w-6 rounded-full border-2 transition-transform ${color === cHex ? "border-zinc-900 scale-110" : "border-white ring-1 ring-zinc-200"}`}
            style={{ backgroundColor: cHex }} />
        ))}
        <span className="w-px h-6 bg-zinc-200 mx-1" />
        <input type="range" min="1" max="10" value={strokeWidth} onChange={(e) => setStrokeWidth(+e.target.value)}
          data-testid="markup-width" className="w-20 accent-zinc-900" title="Stroke width" />
        <span className="w-px h-6 bg-zinc-200 mx-1" />
        <button onClick={undo} title="Undo" data-testid="markup-undo"
          className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors">
          <Undo2 className="h-4 w-4" />
        </button>
        <button onClick={clearAll} title="Clear all" data-testid="markup-clear"
          className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Canvas stack */}
      <div ref={wrapRef} className="flex-1 min-h-0 overflow-auto bg-zinc-200 py-4 flex flex-col items-center gap-4">
        {pages.map((pg, i) => (
          <div key={i} className="shadow-md bg-white">
            <canvas ref={(el) => { canvasEls.current[i] = el; }} data-testid={`markup-canvas-${i}`} />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 pt-3 border-t border-zinc-200">
        <button onClick={handleSave} disabled={saving} data-testid="markup-save"
          className="inline-flex items-center gap-2 bg-zinc-900 text-white h-10 px-4 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {saving ? "Saving…" : "Save as new version"}
        </button>
        <button onClick={onCancel} disabled={saving} data-testid="markup-cancel"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm font-semibold hover:bg-zinc-100 transition-colors">
          <X className="h-4 w-4" /> Cancel
        </button>
        <span className="text-xs text-zinc-400 ml-auto">{isPdf ? "Saved as a new PDF" : "Saved as a new PNG"}</span>
      </div>
    </div>
  );
}
